import { deleteStore, listOfStore } from "Api/Store";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import DeleteModal from "Components/Base/DeleteModal";
import {
  checkStatusCodeSuccess,
  notFound,
  Search,
} from "Components/constants/Common";
import {
  ActionKey,
  ActionLabel,
  DeleteLabel,
  editLabel,
  NewLabel,
} from "Components/constants/Role";
import {
  ContactNoKey,
  EmailKey,
  EmailLabel,
  PhoneNumberLabel,
} from "Components/constants/Staff";
import {
  STORE_LABEL,
  STORE_NAME,
  STORE_NAME_ACCESS_KEY,
} from "Components/constants/Store";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Store = () => {
  document.title = STORE_LABEL;
  const navigate = useNavigate();
  const [storeData, setStoreData] = useState<Array<any>>([]);
  const [storeSearchData, setStoreSearchData] = useState<Array<any>>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number>();
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleDelete = (id: string) => {
    setDeleteOpen(!deleteOpen);
    setDeleteId(Number(id));
  };

  const handleSearchValueChange = (value: any) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const storeColumns = useMemo(
    () => [
      {
        header: STORE_NAME,
        accessorKey: STORE_NAME_ACCESS_KEY,
        enableColumnFilter: false,
      },
      {
        header: EmailLabel,
        accessorKey: EmailKey,
        enableColumnFilter: false,
      },
      {
        header: PhoneNumberLabel,
        accessorKey: ContactNoKey,
        enableColumnFilter: false,
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => (
          <div className="d-flex justify-content-start enable-action flex-wrap gap-2 icon">
            <span className="tooltip-container">
              <i
                className="mdi mdi-pencil-outline fs-4 text-info cursor-pointer"
                onClick={() =>
                  navigate(`/edit-store/${cell?.row?.original?.id}`)
                }
              ></i>
              <span className="tooltip-text">{editLabel}</span>
            </span>
            {/* delete future purpose */}
            {/* <span className="tooltip-container">
              <i
                className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
                onClick={() => handleDelete(cell?.row?.original?.id)}
              ></i>
              <span className="tooltip-text">{DeleteLabel}</span>
            </span> */}
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: true,
      },
    ],
    []
  );

  const deleteStoreData = () => {
    setBtnLoader(true);
    deleteStore(Number(deleteId))
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res?.message);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error?.message);
      })
      .finally(() => {
        setBtnLoader(false);
        setDeleteOpen(!deleteOpen);
        fetchStoreData();
      });
  };

  const fetchStoreData = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      limit: customPageSize,
    };
    listOfStore(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setStoreData(res?.data?.stores);
          setTotalRecords(res?.data?.totalItems);
          setTotalPages(res?.data?.totalPages);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          if (searchValue) {
            fetchSearchStoreData();
          }
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchSearchStoreData = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      search: searchValue,
      limit: customPageSize,
    };
    listOfStore(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setStoreSearchData(response?.data?.stores);
          setTotalRecords(response?.data?.totalItems);
          setTotalPages(response?.data?.totalPages);
          setTotalNumberOfRows(response?.data?.numberOfRows);
        }
      })
      .catch((error) => {
        setStoreSearchData([]);
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (searchValue === "") {
      setCurrentPage(1);
    }
  }, [searchValue]);

  useEffect(() => {
    if (searchValue) {
      fetchSearchStoreData();
    } else {
      fetchStoreData();
    }
  }, [currentPage, customPageSize, sortOrder, columnName, searchValue]);

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{STORE_LABEL}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/add-store")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>
        <div className="card py-2 px-4 overflow-x-auto">
          {loader || storeData.length > 0 ? (
            <TableContainer
              customPageSize={customPageSize}
              setCustomPageSize={setCustomPageSize}
              isGlobalFilter={true}
              columns={storeColumns}
              data={searchValue ? storeSearchData : storeData || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearchValueChange}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
        <DeleteModal
          btnLoader={Boolean(btnLoader)}
          onCloseClick={() => setDeleteOpen(!deleteOpen)}
          onDeleteClick={() => deleteStoreData()}
          show={deleteOpen}
        />
      </div>
    </div>
  );
};

export default Store;
